import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { AssetClass, BlotterType } from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Subject } from "rxjs";
import { throttleTime } from "rxjs/operators";
import { BfxBlotterFilterBarItem } from "../bfx-blotter-filter/bfx-blotter-filter-bar.constants";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import {
  BfxBlotterFilter,
  BfxBlotterFilterId,
} from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterable } from "../bfx-blotter-filter/bfx-blotter-filterable";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { OpsIntBlotterFilterListService } from "./services/bfx-blotter-ops-int-filter-list.service";
import { BfxBlotterOpsIntService } from "./services/bfx-blotter-ops-int.service";
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";

const BLOTTER_FILTER_TEMPERATURE_NODE_NAME = "BFX-BLOTTER-FILTER-TEMPERATURE";

@Component({
  selector: "bfx-blotter-ops-int-blotter-filter-bar",
  templateUrl: "./html/bfx-blotter-ops-int-blotter-filter-bar.html",
  standalone: false,
})
export class BfxBlotterOpsInBlotterFilterBarComponent implements OnInit, DoCheck {
  @Input()
  public blotterInstance: BfxBlotterFilterable = null;

  public filters: Array<BfxBlotterFilter> = null;
  public filterConfiguration: string[] = null;
  private checkOverflowSubject: Subject<void> = new Subject<void>();
  /** Filters that are not visible */
  private dropDownItems: Array<BfxBlotterFilterBarItem> = [];
  /** Menu items for filters that are not visible */
  public menuItems: Array<any> = [];

  public overflows: boolean = false;
  public blotterId: string = null;
  public blotterType: BlotterType = BlotterType.OPSINTERVENTION;

  public items: Array<BfxBlotterFilterBarItem> = [];
  public showDropdown: boolean = false;

  @ViewChild(TieredMenu) menu: TieredMenu;
  @ViewChild("menuItemsContainer") menuContainer: ElementRef;

  public window: NativeDesktopWindow = null;
  public lastShowTime: number = 0;

  constructor(
    public service: BfxBlotterFilterService,
    public element: ElementRef,
    private goldenLayoutServices: GoldenLayoutService,
    protected opsIntBlotterFilterList: OpsIntBlotterFilterListService,
    private ref: ChangeDetectorRef,
    public blotterService: BfxBlotterOpsIntService,
  ) {}

  ngOnInit() {
    this.initializeBlotterInstance();
    this.service.modelUpdateSubject.subscribe(this.refresh.bind(this));
    this.service.resizeSubject.subscribe(this.handleResize.bind(this));
    this.service.filterConfiguration$.subscribe(this.refresh.bind(this));
    this.checkOverflowSubject.pipe(throttleTime(300)).subscribe(this.updateOverflow.bind(this));
  }

  ngDoCheck() {
    this.checkOverflow();
  }

  protected initializeBlotterInstance() {
    if (this.blotterInstance != null) {
      this.blotterId = this.blotterInstance.getBlotterId();
      this.filterConfiguration = this.blotterService.getFilterConfiguration();
      this.refresh(true);
      this.blotterInstance.setBlotterFilterComponent(this);
    }
  }

  public get allItems(): Array<BfxBlotterFilterBarItem> {
    return this.items;
  }

  public getSelectedItems(): Array<BfxBlotterFilterBarItem> {
    return this.items.filter((item: BfxBlotterFilterBarItem) => item.selected);
  }

  public getSelectedFilters(): Array<BfxBlotterFilter> {
    let selectedFilterIds: Array<string> = this.items
      .filter((item: BfxBlotterFilterBarItem) => item.selected)
      .map((item: BfxBlotterFilterBarItem) => item.filterId);
    return this.filters
      ? this.filters.filter(
          (filter: BfxBlotterFilter) => selectedFilterIds.indexOf(filter.filterId) >= 0,
        )
      : [];
  }

  public refresh(updateFiltersAfterwards: boolean = false): void {
    // Refresh is called from the filter configuration subject, which seems to be hit each
    // time there is a filter change on any blotter
    let skipUpdate: boolean =
      updateFiltersAfterwards != null &&
      (updateFiltersAfterwards as any).blotterId != null &&
      (updateFiltersAfterwards as any).blotterId != this.blotterId;

    if (!skipUpdate) {
      let filterId: BfxBlotterFilterId = new BfxBlotterFilterId(this.blotterType, AssetClass.FIX);

      let defaultFilterList = OpsIntBlotterFilterListService.getAllCategoryFilters(
        this.opsIntBlotterFilterList,
      );
      this.filters = defaultFilterList;
      this.items = new Array<BfxBlotterFilterBarItem>();
      this.filterConfiguration = this.blotterInstance.getFilterConfiguration();
      let selected: Array<string> = this.filterConfiguration;

      defaultFilterList.forEach((filter: BfxBlotterFilter) => {
        let item: BfxBlotterFilterBarItem = new BfxBlotterFilterBarItem();

        item.filterId = filter.filterId;
        item.filterName = filter.filterName;
        item.favorited = false;
        item.selected = selected != null && selected.indexOf(filter.filterId) >= 0;
        this.items.push(item);
      });

      this.updateItems();

      if (updateFiltersAfterwards && this.blotterInstance.filterSelectionChanged) {
        this.blotterInstance.filterSelectionChanged(this.filterConfiguration);
      }
    }
  }

  public selectItem(selectedItem: BfxBlotterFilterBarItem, event: any): void {
    const lastFilterUnselected =
      this.filterConfiguration.length == 1 &&
      this.filterConfiguration.indexOf(selectedItem.filterId) > -1;
    if (!lastFilterUnselected) {
      selectedItem.selected = !selectedItem.selected;

      this.updateItems();

      let index: number = this.filterConfiguration.indexOf(selectedItem.filterId);

      if (index < 0 && selectedItem.selected) {
        this.filterConfiguration.push(selectedItem.filterId);
      } else if (index >= 0 && !selectedItem.selected) {
        this.filterConfiguration.splice(index, 1);
      }

      if (this.blotterInstance.filterSelectionChanged) {
        this.blotterInstance.filterSelectionChanged(this.filterConfiguration);
        if (this.blotterInstance.hasCellRenderedSelection()) {
          this.blotterInstance.refreshSelectionCells();
        }
      }

      this.checkOverflow();

      if (this.window != null) {
        this.window.hide();
      }
    }
  }

  protected handleResize(event: any): void {
    if (this.window != null) {
      this.window.hide();
      return;
    }

    this.showDropdown = false;
    this.checkOverflow();
  }

  protected updateOverflow(): void {
    let nodes: Array<any> = this.element.nativeElement.childNodes;
    let overflow: boolean = false;
    let reference: number = nodes[0].parentElement.offsetTop;
    // clean-up non-filter nodes
    let clearedNodeList = Object.values(nodes).filter(
      (elem: HTMLDivElement) =>
        elem.nodeName !== BLOTTER_FILTER_TEMPERATURE_NODE_NAME && !(elem instanceof Comment),
    );

    this.dropDownItems = [];
    for (let i: number = 0; i < clearedNodeList.length; i++) {
      let isNodeOverflowed: boolean =
        clearedNodeList[i].offsetTop != null && clearedNodeList[i].offsetTop - reference > 10;
      overflow = overflow || isNodeOverflowed;
      if (overflow) {
        let dropDown: Array<BfxBlotterFilterBarItem> = this.items.filter(
          (item: BfxBlotterFilterBarItem) => item.filterName == clearedNodeList[i].innerText,
        );
        if (dropDown.length == 1) {
          this.dropDownItems.push(dropDown[0]);
        } else {
          overflow = false;
        }
      }

      this.overflows = overflow;
    }
  }

  public updateItems(): void {
    // Update filter configuration
    this.updateFilterConfiguration();

    // Check if bar overflows and show dropdown
    this.checkOverflow();

    //update tabs title
    this.updateTabs();
  }

  protected compareItems(itemA: BfxBlotterFilterBarItem, itemB: BfxBlotterFilterBarItem): number {
    return itemA.selected == itemB.selected
      ? itemA.filterName.localeCompare(itemB.filterName)
      : itemA.selected
        ? -1
        : 1;
  }

  protected updateFilterConfiguration(): void {
    if (this.filterConfiguration != null) {
      this.filterConfiguration.splice(0, this.filterConfiguration.length);
    } else {
      this.filterConfiguration = [];
    }

    // Filter ID for logging
    const logIds = [];
    this.items.forEach((item: BfxBlotterFilterBarItem) => {
      if (item.selected) {
        this.filterConfiguration.push(item.filterId);
        logIds.push(`${item.filterName}(${item.filterId})`);
      }
    });
    // Log out the active list of filters
    logger.out.info(
      `opsfb-c - ${this.blotterId}: applying ${logIds.length} filters [${logIds.join(",")}]`,
    );

    this.blotterService.updateFilterSelection(this.filterConfiguration);
  }

  public toggleDropdown(event: any): void {
    this.menuItems = this.constructMenu();
    this.menu.toggle(event);
  }

  private constructMenu() {
    const retItems: any[] = this.dropDownItems.map((item) => {
      return {
        label: item.filterName,
        command: (event) => this.selectItem(item, event),
        show: true,
        styleClass: item.selected ? "filter-selected" : "filter-unselected",
      };
    });
    return retItems;
  }

  private updateTabs(): void {
    this.blotterService.updateBadgeCount(this.blotterInstance);
  }

  protected checkOverflow(): void {
    this.checkOverflowSubject.next();
  }
}
